import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

const NAV = [
  { to: "/", label: "Anasayfa" },
  { to: "/urunler", label: "Ürünler" },
  { to: "/urunler/mumlar", label: "Mumlar" },
  { to: "/urunler/sabunlar", label: "Sabunlar" },
  { to: "/kisiye-ozel", label: "Kişiye Özel" },
  { to: "/hikayemiz", label: "Hikâyemiz" },
  { to: "/iletisim", label: "İletişim" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { count } = useCart();
  const location = useLocation();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-larc-bg text-larc-ink font-body" data-testid="public-layout">
      {/* Announcement bar */}
      {settings?.announcement_bar && (
        <div className="bg-larc-ink text-larc-bg text-xs tracking-[0.18em] uppercase py-2 text-center font-medium" data-testid="announcement-bar">
          {settings.announcement_bar}
        </div>
      )}

      {/* Header */}
      <header className={`sticky top-0 z-40 transition-all duration-300 backdrop-blur-xl ${scrolled ? "bg-white/85 border-b border-larc-border" : "bg-white/60"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-larc-gold/40 bg-white/60">
              <span className="block h-3 w-3 rounded-full bg-larc-gold" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-heading text-2xl italic text-larc-ink tracking-tight">{settings?.logo_text || "Larcraft"}</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-larc-muted mt-0.5">{settings?.logo_subtitle || "atelier — el yapımı"}</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `text-sm tracking-wide transition-colors ${isActive ? "text-larc-ink" : "text-larc-muted hover:text-larc-ink"}`}
                data-testid={`nav-${n.to.replace(/\W+/g, "") || "home"}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/sepet" className="relative inline-flex items-center justify-center h-10 w-10 rounded-full border border-larc-border hover:border-larc-gold transition-colors" data-testid="cart-link">
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-larc-gold text-white text-[10px] font-medium flex items-center justify-center" data-testid="cart-count">{count}</span>
              )}
            </Link>
            <button className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-larc-border" onClick={() => setOpen((v) => !v)} data-testid="menu-toggle">
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-larc-border bg-white" data-testid="mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className="text-base text-larc-ink py-1">{n.label}</NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[60vh]">
        <Outlet context={{ settings }} />
      </main>

      <Footer settings={settings} />
    </div>
  );
}

function Footer({ settings }) {
  return (
    <footer className="mt-32 border-t border-larc-border bg-larc-alt" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-larc-gold/40 bg-white/60">
              <span className="block h-3 w-3 rounded-full bg-larc-gold" />
            </span>
            <span className="font-heading text-3xl italic">{settings?.logo_text || "Larcraft"}</span>
          </div>
          <p className="mt-5 text-sm text-larc-muted leading-relaxed max-w-md">
            Küçük partilerle, sevgiyle hazırlanan el yapımı mum ve sabun atölyesi. Her ürün, atölyemizde tek tek dökülür ve özenle paketlenir.
          </p>
          <a href={settings?.instagram_url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm mt-6 text-larc-ink hover:text-larc-gold transition-colors" data-testid="footer-instagram">
            <Instagram size={16} /> @larcraft.shop
          </a>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-larc-gold mb-4">Keşfet</div>
          <ul className="space-y-2.5 text-sm text-larc-muted">
            <li><Link to="/urunler/mumlar" className="hover:text-larc-ink">Mumlar</Link></li>
            <li><Link to="/urunler/sabunlar" className="hover:text-larc-ink">Sabunlar</Link></li>
            <li><Link to="/urunler/hediye-setleri" className="hover:text-larc-ink">Hediye Setleri</Link></li>
            <li><Link to="/kisiye-ozel" className="hover:text-larc-ink">Kişiye Özel</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-larc-gold mb-4">İletişim</div>
          <ul className="space-y-2.5 text-sm text-larc-muted">
            <li>{settings?.contact_email || "hello@larcraft.shop"}</li>
            <li>{settings?.phone || "+90 555 000 00 00"}</li>
            <li>{settings?.address || "İstanbul, Türkiye"}</li>
            <li>{settings?.working_hours || "Hafta içi 10:00 - 18:00"}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-larc-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-larc-muted">
          <div>© {new Date().getFullYear()} {settings?.brand_name || "Larcraft"}. Tüm hakları saklıdır.</div>
          <div>el yapımı · küçük parti · sevgiyle</div>
        </div>
      </div>
    </footer>
  );
}
